import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { getCategoryQueryKey } from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import { queryClient } from '@/services';
import { IntlCategoryDTO } from '@/services/content-api-types';

import { useAdminManagingCountry } from './useAdminManagingCountry';

import {
  getCategoryByCountryCodeQueryKey,
  saveCategory,
} from '@/_generated/api-clients/content';

export const useIsAdmin = () => {
  const { data } = useAuth();

  return Constants.USER_ROLES.ADMIN === data?.highestRoleLevel;
};

export const useEditCategory = () => {
  const { t } = useTranslation();
  const { countryCode } = useAdminManagingCountry();

  return useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IntlCategoryDTO;
      categoryName?: string;
      onSuccess?: () => void;
    }) =>
      saveCategory({
        body: {
          ...payload,
          country: payload.country ?? countryCode,
        },
      }),
    onSuccess: (_data, { payload, categoryName, onSuccess }) => {
      const country = payload.country ?? countryCode;

      queryClient.invalidateQueries({
        queryKey: getCategoryByCountryCodeQueryKey({
          query: { country },
        }),
      });

      if (payload.id) {
        queryClient.invalidateQueries({
          queryKey: getCategoryQueryKey({
            query: { country },
            path: { id: payload.id },
          }),
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['browse'],
      });

      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {t('Admin.updateCategoryToast', { categoryName })}
        </ToastMessageBlock>
      ));
      onSuccess?.();
    },
    onError: err => {
      console.error(err);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {Constants.IS_IN_CYPRESS_TEST_LOCAL || Constants.IS_IN_JEST_TEST
            ? 'UpdateCategory Fail In Cypress Test'
            : err.message}
        </ToastMessageBlock>
      ));
      datadogRum.addError(err);
    },
  });
};
